import { getMap, saveMap, setPreferences } from "@/src/core/persistence/repository";
import type { PersistedPreferences } from "@/src/core/persistence/schema";
import { useMapStore } from "@/src/store/useMapStore";

export const AUTOSAVE_DELAY_MS = 1000;

/** The slices of store state a save actually depends on. */
const mapFingerprint = (state: ReturnType<typeof useMapStore.getState>) => [
  state.currentMapId,
  state.meta,
  state.floorPolygons,
  state.hexCells,
  state.objects,
  state.terrainPatches,
  state.roomKeys,
  state.viewport,
  state.nextRoomNumber,
] as const;

const preferenceFingerprint = (state: ReturnType<typeof useMapStore.getState>) => [
  state.language,
  state.selectedStampAsset,
  state.selectedTerrain,
  state.repeatPlacement,
  state.annotationKind,
  state.annotationText,
  state.annotationFontSize,
  state.annotationColor,
] as const;

const changed = (a: readonly unknown[], b: readonly unknown[]) =>
  a.some((value, index) => value !== b[index]);

const readPreferences = (
  state: ReturnType<typeof useMapStore.getState>,
): PersistedPreferences => ({
  language: state.language,
  selectedStampAsset: state.selectedStampAsset,
  selectedTerrain: state.selectedTerrain,
  repeatPlacement: state.repeatPlacement,
  annotationKind: state.annotationKind,
  annotationText: state.annotationText,
  annotationFontSize: state.annotationFontSize,
  annotationColor: state.annotationColor,
});

export interface AutosaveOptions {
  delayMs?: number;
  onSaved?: (savedAt: number) => void;
  onError?: (error: unknown) => void;
}

/**
 * Mirrors the store into IndexedDB on a debounce.
 *
 * The store stays the single source of truth: this only ever reads from it and
 * writes outward. It never pushes state back in, so there is no second brain
 * that could disagree with the editor.
 */
export const startAutosave = ({
  delayMs = AUTOSAVE_DELAY_MS,
  onSaved,
  onError,
}: AutosaveOptions = {}): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> = Promise.resolve();
  let disposed = false;

  const flush = () => {
    timer = null;
    const state = useMapStore.getState();
    const id = state.currentMapId;
    if (!state.hydrated || !id) return;

    // Chain rather than race: two quick saves must not interleave.
    inFlight = inFlight
      .then(async () => {
        if (disposed) return;
        const existing = await getMap(id);
        if (!existing) return; // Deleted from the library mid-edit.
        const saved = await saveMap({
          ...existing,
          document: state.getDocument(),
          viewport: state.viewport,
          nextRoomNumber: state.nextRoomNumber,
        });
        await setPreferences(readPreferences(state));
        onSaved?.(saved.updatedAt);
      })
      .catch((error: unknown) => {
        onError?.(error);
      });
  };

  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, delayMs);
  };

  let previousMap = mapFingerprint(useMapStore.getState());
  let previousPreferences = preferenceFingerprint(useMapStore.getState());

  const unsubscribe = useMapStore.subscribe((state) => {
    if (!state.hydrated) return;
    const nextMap = mapFingerprint(state);
    const nextPreferences = preferenceFingerprint(state);
    const dirty = changed(nextMap, previousMap) || changed(nextPreferences, previousPreferences);
    previousMap = nextMap;
    previousPreferences = nextPreferences;
    if (dirty) schedule();
  });

  return () => {
    disposed = true;
    if (timer) clearTimeout(timer);
    unsubscribe();
  };
};
